import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { useSubscriptionContext } from '../context/SubscriptionContext';

interface SubscriptionStatusBadgeProps {
  showDetails?: boolean;
}

const SubscriptionStatusBadge: React.FC<SubscriptionStatusBadgeProps> = ({ showDetails = false }) => {
  const { subscription, enterprise, loading } = useSubscriptionContext();
  const normalizeName = (v: string) => {
    const spaced = (v || '').replace(/_/g, ' ').trim();
    return spaced.split(' ').map(s => s ? s[0].toUpperCase() + s.slice(1) : s).join(' ');
  };
  // Check if there's no subscription at all
  const hasNoSubscription = !enterprise && !subscription?.subscription;
  
  const isTrialing = subscription?.subscription?.status === 'trialing';
  const trialEnd = subscription?.subscription?.trial_end 
    ? new Date(subscription.subscription.trial_end * 1000)
    : null;
  
  const currentPlan = {
    tier: enterprise ? 'enterprise' : (subscription?.tier || (hasNoSubscription ? 'none' : 'free')),
    isActive: enterprise ? true : (subscription?.subscription?.status === 'active' || isTrialing),
    planName: enterprise ? 'Enterprise' : (subscription?.plan_badge_name || (hasNoSubscription ? 'None' : normalizeName(subscription?.tier || 'free'))),
    subscriptionId: subscription?.subscription?.id || null,
    nextBillingDate: subscription?.subscription?.current_period_end
      ? new Date(subscription.subscription.current_period_end * 1000).toLocaleDateString()
      : null,
    willCancel: Boolean(subscription?.subscription?.cancel_at_period_end),
    badgeColor: subscription?.plan_badge_color || undefined,
    isTrialing: isTrialing,
    trialEnd: trialEnd,
  };
  
  // Stabilize badge: if we have an active Stripe subscription or a custom badge, don't show a misleading Free state
  const effectiveTier = (() => {
    if (enterprise) return 'enterprise';
    if (hasNoSubscription) return 'none';
    const hasCustomBadge = Boolean(subscription?.plan_badge_name);
    const hasActiveStripeSub = Boolean(subscription?.subscription?.status === 'active' || subscription?.subscription?.status === 'trialing');
    if (hasCustomBadge || hasActiveStripeSub) {
      return subscription?.tier && subscription.tier !== 'free' ? subscription.tier : 'paid';
    }
    return subscription?.tier || 'free';
  })();

  if (loading) {
    return <Chip label="Updating..." size="small" variant="outlined" />;
  }

  const getStatusColor = () => {
    if (effectiveTier === 'enterprise') {
      return 'secondary'; // Purple for Enterprise
    }
    if (effectiveTier === 'none') {
      return 'default'; // Gray for None
    }
    if (currentPlan.willCancel) {
      return 'warning';
    }
    // Any active paid tier (not free/enterprise/none) => success
    const isPaid = effectiveTier !== 'free' && effectiveTier !== 'enterprise' && effectiveTier !== 'none';
    if (currentPlan.isActive && isPaid) {
      return 'success';
    }
    if (effectiveTier === 'free') {
      return 'primary'; // Blue for Free
    }
    return 'default';
  };

  const getStatusLabel = () => {
    if (effectiveTier === 'enterprise') return 'Enterprise';
    if (effectiveTier === 'none') return 'None';
    if (currentPlan.willCancel) return `${currentPlan.planName} (Canceling)`;
    if (currentPlan.isActive && effectiveTier !== 'free' && effectiveTier !== 'none') return currentPlan.planName;
    if (effectiveTier === 'free') return 'Free';
    return currentPlan.planName || 'None';
  };

  return (
    <Box display="flex" alignItems="center" gap={0}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Chip
          label={getStatusLabel()}
          color={getStatusColor() as any}
          size="small"
          variant={currentPlan.badgeColor ? 'outlined' : 'filled'}
          sx={currentPlan.badgeColor ? {
            backgroundColor: currentPlan.badgeColor,
            color: '#fff',
            borderColor: 'divider',
            '& .MuiChip-label': {
              color: '#fff',
              fontWeight: 500,
            },
          } : undefined}
        />
        {showDetails && currentPlan.isTrialing && currentPlan.trialEnd && (
          <Typography variant="caption" color="success.main" sx={{ fontStyle: 'italic', mt: 0.25, fontSize: '0.65rem', lineHeight: 1, textAlign: 'center', width: '100%', fontWeight: 500 }}>
            Trial ends {currentPlan.trialEnd.toLocaleDateString()}
          </Typography>
        )}
        {showDetails && currentPlan.willCancel && currentPlan.nextBillingDate && !currentPlan.isTrialing && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.25, fontSize: '0.65rem', lineHeight: 1, textAlign: 'center', width: '100%' }}>
            Until {currentPlan.nextBillingDate}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionStatusBadge;
